/**
 * Bilingual labels for every enum the API returns (camelCase values, see the backend contract) and the lifecycle catalogue of the
 * default Federal Public Policy template (services/src/Policy.Application/Lifecycle/DefaultTemplates.cs). Arabic is formal Modern
 * Standard Arabic as used in federal government correspondence.
 */
export interface Bilingual {
  en: string;
  ar: string;
}

const L = (en: string, ar: string): Bilingual => ({ en, ar });

export const POLICY_STATUSES = [
  "draft", "inProgress", "submitted", "underReview", "returned", "endorsed", "onCabinetAgenda", "approved", "published", "inForce",
  "underEvaluation", "amending", "sunset", "archived", "withdrawn", "rejected",
] as const;
export type PolicyStatus = (typeof POLICY_STATUSES)[number];

/** The Cabinet Guidebook's instrument types (framework, public policy, strategy, legislation, national programme, plan), with legislation split by form. */
export const INSTRUMENT_TYPES = [
  "publicPolicy", "framework", "strategy", "nationalProgramme", "plan",
  "federalLaw", "decreeLaw", "cabinetResolution", "executiveRegulation", "ministerialResolution", "guideline",
] as const;
export type InstrumentType = (typeof INSTRUMENT_TYPES)[number];

export const CLASSIFICATIONS = ["public", "internal", "confidential", "secret"] as const;
export type Classification = (typeof CLASSIFICATIONS)[number];

export const PRIORITIES = ["low", "normal", "high", "urgent"] as const;
export type Priority = (typeof PRIORITIES)[number];

export const SLA_HEALTHS = ["onTrack", "atRisk", "breached", "paused", "completed"] as const;
export type SlaHealth = (typeof SLA_HEALTHS)[number];

export const DISCLOSURE_LEVELS = ["private", "pipelineCard", "shared", "submitted", "published"] as const;
export type DisclosureLevel = (typeof DISCLOSURE_LEVELS)[number];

export const TENANT_KINDS = ["ministry", "oversight", "platform"] as const;
export type TenantKind = (typeof TENANT_KINDS)[number];

export const STAGE_KEYS = ["s1_priority", "s2_diagnosis", "s3_design", "s4_appraisal", "s5_implementation", "s6_builder", "s7_gsoc", "s8_cabinet", "s9_monitoring", "s10_evaluation"] as const;
export type StageKey = (typeof STAGE_KEYS)[number];

export const PHASE_KEYS = ["phase1", "phase2", "pmo", "phase3"] as const;
export type PhaseKey = (typeof PHASE_KEYS)[number];

export interface StageDefinition {
  key: StageKey;
  order: number;
  phase: PhaseKey;
  owner: "ministry" | "pmo" | "cabinet";
  gateKey: string;
  name: Bilingual;
  /** Compact label for steppers and board columns. */
  short: Bilingual;
  gate: Bilingual;
  slaDays: number;
}

export const PHASES: Record<PhaseKey, Bilingual> = {
  phase1: L("Phase 1 · Problem, objectives and design", "المرحلة الرئيسية الأولى · المشكلة والأهداف والتصميم"),
  phase2: L("Phase 2 · Appraisal, planning and ministry sign-off", "المرحلة الرئيسية الثانية · التقييم والتخطيط واعتماد الوزارة"),
  pmo: L("GSOC review and Cabinet decision", "مراجعة الأمانة العامة لمجلس الوزراء وقرار مجلس الوزراء"),
  phase3: L("Phase 3 · Monitoring and evaluation", "المرحلة الرئيسية الثالثة · المتابعة والتقييم"),
};

export const PHASE_SHORT: Record<PhaseKey, Bilingual> = {
  phase1: L("Phase 1", "المرحلة الرئيسية الأولى"),
  phase2: L("Phase 2 · Ministry", "المرحلة الرئيسية الثانية · الوزارة"),
  pmo: L("GSOC and Cabinet", "الأمانة العامة ومجلس الوزراء"),
  phase3: L("Phase 3", "المرحلة الرئيسية الثالثة"),
};

export const STAGES: readonly StageDefinition[] = [
  { key: "s1_priority", order: 1, phase: "phase1", owner: "ministry", gateKey: "g1", slaDays: 20, name: L("Priority setting and problem identification", "تحديد الأولويات وتعريف المشكلة"), short: L("Priority & problem", "الأولويات والمشكلة"), gate: L("Gate 1: problem accepted", "البوابة 1: اعتماد تعريف المشكلة") },
  { key: "s2_diagnosis", order: 2, phase: "phase1", owner: "ministry", gateKey: "g2", slaDays: 20, name: L("Diagnosis, justification and objectives", "التشخيص والمبررات والأهداف"), short: L("Diagnosis & objectives", "التشخيص والأهداف"), gate: L("Gate 2: objectives approved", "البوابة 2: اعتماد الأهداف") },
  { key: "s3_design", order: 3, phase: "phase1", owner: "ministry", gateKey: "g3", slaDays: 25, name: L("Policy design and options", "تصميم السياسة والخيارات"), short: L("Design & options", "التصميم والخيارات"), gate: L("Gate 3: options shortlisted", "البوابة 3: اعتماد القائمة المختصرة للخيارات") },
  { key: "s4_appraisal", order: 4, phase: "phase2", owner: "ministry", gateKey: "g4", slaDays: 25, name: L("Appraisal, impact and decision support", "التقييم والأثر ودعم القرار"), short: L("Appraisal & impact", "التقييم والأثر"), gate: L("Gate 4: preferred option endorsed", "البوابة 4: اعتماد الخيار الأمثل") },
  { key: "s5_implementation", order: 5, phase: "phase2", owner: "ministry", gateKey: "g5", slaDays: 20, name: L("Implementation readiness, delivery and communication", "جاهزية التنفيذ والإنجاز والاتصال"), short: L("Implementation plan", "خطة التنفيذ"), gate: L("Gate 5: delivery readiness confirmed", "البوابة 5: تأكيد جاهزية التنفيذ") },
  { key: "s6_builder", order: 6, phase: "phase2", owner: "ministry", gateKey: "g6", slaDays: 15, name: L("Policy Builder", "إعداد وثائق السياسة"), short: L("Policy Builder & sign-off", "إعداد الوثائق والاعتماد"), gate: L("Gate 6: ministry sign-off and submission", "البوابة 6: اعتماد الوزارة والتقديم") },
  { key: "s7_gsoc", order: 7, phase: "pmo", owner: "pmo", gateKey: "g7", slaDays: 15, name: L("GSOC review", "مراجعة الأمانة العامة لمجلس الوزراء"), short: L("GSOC review", "مراجعة الأمانة العامة"), gate: L("Gate 7: GSOC decision", "البوابة 7: قرار الأمانة العامة لمجلس الوزراء") },
  { key: "s8_cabinet", order: 8, phase: "pmo", owner: "cabinet", gateKey: "g8", slaDays: 30, name: L("Cabinet decision and issuance", "قرار مجلس الوزراء والإصدار"), short: L("Cabinet decision", "قرار مجلس الوزراء"), gate: L("Gate 8: Cabinet decision recorded", "البوابة 8: تسجيل قرار مجلس الوزراء") },
  { key: "s9_monitoring", order: 9, phase: "phase3", owner: "ministry", gateKey: "g9", slaDays: 365, name: L("Monitoring and adaptation", "المتابعة والتحسين"), short: L("Monitoring", "المتابعة"), gate: L("Gate 9: evaluation opened", "البوابة 9: بدء التقييم") },
  { key: "s10_evaluation", order: 10, phase: "phase3", owner: "ministry", gateKey: "g10", slaDays: 60, name: L("Evaluation and review", "التقييم والمراجعة"), short: L("Evaluation", "التقييم والمراجعة"), gate: L("Gate 10: evaluation decision", "البوابة 10: قرار التقييم") },
];

export const REGLAB_BRANCH: Bilingual = L("UAE Regulations Lab pilot", "تجربة مختبر التشريعات في دولة الإمارات");

export const PILOT_PHASES: ReadonlyArray<{ key: string; name: Bilingual }> = [
  { key: "application", name: L("Application", "تقديم الطلب") },
  { key: "review", name: L("Lab review", "مراجعة المختبر") },
  { key: "permit", name: L("Cabinet approval and licence", "موافقة مجلس الوزراء والترخيص") },
  { key: "testing_reporting", name: L("Testing and reporting", "الاختبار ورفع التقارير") },
  { key: "impact_assessment", name: L("Impact assessment", "تقييم الأثر") },
  { key: "decision", name: L("Pilot decision", "قرار التجربة") },
];

/** Every enum group, value -> label. Groups are addressed as `enums.<group>.<value>` in the i18next catalogues. */
export const ENUMS = {
  policyStatus: {
    draft: L("Draft", "مسودة"),
    inProgress: L("In progress", "قيد الإعداد"),
    submitted: L("Submitted", "مقدَّمة"),
    underReview: L("Under GSOC review", "قيد مراجعة الأمانة العامة"),
    returned: L("Returned to ministry", "مُعادة إلى الوزارة"),
    endorsed: L("Endorsed", "مؤيَّدة"),
    onCabinetAgenda: L("On Cabinet agenda", "مدرجة على جدول أعمال مجلس الوزراء"),
    approved: L("Approved by Cabinet", "معتمدة من مجلس الوزراء"),
    published: L("Published", "منشورة"),
    inForce: L("In force", "سارية"),
    underEvaluation: L("Under evaluation", "قيد التقييم"),
    amending: L("Being amended", "قيد التعديل"),
    sunset: L("Sunset", "منتهية الأجل"),
    archived: L("Archived", "مؤرشفة"),
    withdrawn: L("Withdrawn", "مسحوبة"),
    rejected: L("Not supported", "غير مؤيَّدة"),
  },
  documentKind: {
    Law: L("Federal law", "قانون اتحادي"),
    DecreeLaw: L("Decree-law", "مرسوم بقانون"),
    FederalDecree: L("Federal decree", "مرسوم اتحادي"),
    CabinetResolution: L("Cabinet resolution", "قرار مجلس الوزراء"),
    MinisterialResolution: L("Ministerial resolution", "قرار وزاري"),
    ExecutiveRegulation: L("Executive regulation", "لائحة تنفيذية"),
    Strategy: L("Strategy", "استراتيجية"),
    Policy: L("Policy", "سياسة"),
    Guideline: L("Guideline", "دليل إرشادي"),
    Guidebook: L("Guidebook", "دليل"),
    Manual: L("Tool manual", "دليل استخدام أداة"),
    Report: L("Report", "تقرير"),
    Statistics: L("Statistics", "إحصاءات"),
    Lesson: L("Lesson learned", "درس مستفاد"),
    Evidence: L("Evidence", "دليل إثبات"),
    Other: L("Other", "أخرى"),
  },
  guardrailRule: {
    ArabicStyle: L("Arabic wording to check", "صياغة عربية تحتاج إلى مراجعة"),
    Budget: L("Budget limit", "حد الميزانية"),
    ClassificationCeiling: L("Classification ceiling", "الحد الأعلى للتصنيف"),
    DecisionLanguage: L("Decision language", "صياغة توحي بقرار"),
    LanguageMismatch: L("Arabic text missing", "النص العربي غير موجود"),
    NumberNotFromTool: L("Unverified figures", "أرقام غير متحقق منها"),
    OutputTruncated: L("Answer cut off at the length limit", "اقتُطعت الإجابة عند حد الطول"),
    PiiLeak: L("Personal data", "بيانات شخصية"),
    PromptInjection: L("Instruction-like text in a source", "نص يشبه التعليمات في أحد المصادر"),
    SchemaViolation: L("Answer not in the required format", "إجابة لا تتبع الصيغة المطلوبة"),
    UnsupportedClaim: L("Citation not supported", "استشهاد لا يدعمه المصدر"),
  },
  guardrailAction: {
    blocked: L("Blocked", "حُظر"),
    discarded: L("Discarded", "استُبعد"),
    dropped: L("Removed", "حُذف"),
    flagged: L("Flagged for review", "أُحيل إلى المراجعة"),
    masked: L("Masked", "أُخفي"),
    redacted: L("Sentences removed", "حُذفت بعض الجمل"),
    retried: L("Asked again", "طُلبت الإجابة مجدداً"),
    truncated: L("Complete records kept", "احتُفظ بالسجلات المكتملة"),
    withheld: L("Withheld from AI processing", "حُجب عن المعالجة بالذكاء الاصطناعي"),
    refused: L("Request refused", "رُفض الطلب"),
    "offline fallback": L("Switched to deterministic mode", "حُوِّل إلى الوضع الحتمي"),
  },
  auditEntity: {
    PolicyAssessment: L("Policy assessment", "تقييم السياسة"),
    WebSearch: L("Web search", "البحث على الإنترنت"),
    agenda: L("Agenda", "أجندة السياسات"),
    agent_definition: L("Agent", "وكيل الذكاء الاصطناعي"),
    agent_run: L("Agent run", "تشغيل وكيل الذكاء الاصطناعي"),
    ai_proposal: L("AI proposal", "مقترح الذكاء الاصطناعي"),
    analysis_run: L("Delegated analysis", "تحليل مفوَّض"),
    audit_chain: L("Audit chain", "سلسلة التدقيق"),
    cabinet_agenda_item: L("Cabinet agenda item", "بند في جدول أعمال مجلس الوزراء"),
    cabinet_session: L("Cabinet session", "جلسة مجلس الوزراء"),
    challenge_finding: L("Challenge finding", "ملاحظة المراجعة النقدية"),
    consultation_round: L("Consultation round", "جولة الاستشارة"),
    contact_group: L("Contact group", "مجموعة تواصل"),
    data_connector: L("Data connector", "موصل البيانات"),
    data_series: L("Data series", "سلسلة بيانات"),
    delegation: L("Delegation", "تفويض"),
    directory_entry: L("Directory entry", "إدراج في الدليل"),
    engagement: L("Early advice request", "طلب رأي مبكر"),
    governance_setting: L("Governance setting", "إعداد الحوكمة"),
    identity_provider: L("Identity provider", "مزوّد الهوية"),
    indicator_target: L("Indicator target", "مستهدف المؤشر"),
    insight_report: L("Insight report", "تقرير رصد"),
    inter_ministerial_request: L("Inter-ministerial request", "طلب بين الجهات الاتحادية"),
    knowledge_document: L("Knowledge document", "وثيقة معرفية"),
    lesson: L("Lesson learned", "درس مستفاد"),
    membership: L("Membership", "عضوية"),
    memo: L("Memo", "مذكرة"),
    national_guideline: L("National guideline", "دليل إرشادي وطني"),
    opportunity: L("Policy opportunity", "فرصة سياسة"),
    overlap_alert: L("Overlap alert", "تنبيه تداخل"),
    policy: L("Policy", "سياسة"),
    policy_document: L("Policy document", "وثيقة السياسة"),
    policy_record: L("Policy record", "سجل السياسة"),
    policy_version: L("Policy version", "إصدار السياسة"),
    portfolio_profile: L("Portfolio profile", "ملف الجهة"),
    prompt_version: L("Prompt version", "إصدار التعليمات"),
    registry: L("Registry", "السجل المرجعي"),
    requirement: L("Requirement", "متطلب"),
    requirement_pack: L("Requirement pack", "حزمة متطلبات"),
    role_definition: L("Role", "دور"),
    session: L("Sign-in session", "جلسة الدخول"),
    signal_alert: L("Signal alert", "تنبيه إشارة"),
    studio_session: L("Policy Studio session", "جلسة في استوديو السياسات"),
    submission: L("Submission", "طلب مقدَّم"),
    tenant: L("Workspace", "مساحة العمل"),
    user: L("User", "مستخدم"),
    watch_item: L("Benchmark watchlist item", "عنصر في قائمة رصد المقارنات المعيارية"),
    watch_term: L("Watch term", "مصطلح رصد"),
    workflow_template: L("Workflow template", "قالب مسار العمل"),
  },
  benchmarkDomain: {
    climate: L("Climate", "المناخ"),
    digital: L("Digital and AI", "الرقمنة والذكاء الاصطناعي"),
    economy: L("Economy", "الاقتصاد"),
    education: L("Education", "التعليم"),
    energy: L("Energy", "الطاقة"),
    environment: L("Environment", "البيئة"),
    finance: L("Payments and transfers", "المدفوعات والتحويلات"),
    financial_services: L("Financial services", "الخدمات المالية"),
    food_security: L("Food security", "الأمن الغذائي"),
    governance: L("Governance and regulation", "الحوكمة والتنظيم"),
    health: L("Health", "الصحة"),
    labour: L("Labour market", "سوق العمل"),
    transport: L("Transport", "النقل"),
    water: L("Water", "المياه"),
  },
  approachFamily: {
    behavioural_charge: L("Behavioural charge", "رسوم لتغيير السلوك"),
    demand_management: L("Demand management", "إدارة الطلب"),
    emergency_demand_restrictions: L("Emergency demand restrictions", "قيود طارئة على الطلب"),
    emissions_trading: L("Emissions trading", "تداول الانبعاثات"),
    feed_in_tariff: L("Feed-in tariff", "تعرفة التغذية في الشبكة"),
    fiscal_incentives: L("Fiscal incentives", "حوافز مالية"),
    health_tax: L("Health tax", "ضريبة صحية"),
    intergovernmental_reform: L("Intergovernmental reform", "إصلاح العلاقات بين الحكومات"),
    localisation_quota: L("Localisation quota", "حصص التوطين"),
    public_sector_pilot: L("Public-sector pilot", "تجربة في القطاع الحكومي"),
    regulatory_sandbox: L("Regulatory sandbox", "بيئة تنظيمية تجريبية"),
    reimbursement_flexibility: L("Reimbursement flexibility", "مرونة قواعد استرداد التكاليف"),
    road_pricing: L("Road pricing", "تسعير استخدام الطرق"),
    self_sufficiency_target: L("Self-sufficiency target", "مستهدف الاكتفاء الذاتي"),
    voluntary_pilot: L("Voluntary pilot", "تجربة طوعية"),
  },
  benchmarkInstrument: {
    charge: L("Charge", "رسم"),
    emergency_regulation: L("Emergency regulation", "تنظيم طارئ"),
    excise_tax: L("Excise tax", "ضريبة انتقائية"),
    intergovernmental_agreement: L("Intergovernmental agreement", "اتفاقية بين الحكومات"),
    law: L("Law", "قانون"),
    market_mechanism: L("Market mechanism", "آلية سوقية"),
    national_target: L("National target", "مستهدف وطني"),
    pilot: L("Pilot", "تجربة"),
    pricing_and_standards: L("Pricing and standards", "التسعير والمعايير"),
    quota: L("Quota", "حصة"),
    regulatory_sandbox: L("Regulatory sandbox", "بيئة تنظيمية تجريبية"),
    reimbursement_rule: L("Reimbursement rule", "قاعدة استرداد التكاليف"),
    road_pricing: L("Road pricing", "تسعير استخدام الطرق"),
    tax_incentive: L("Tax incentive", "حافز ضريبي"),
  },
  dataTopic: {
    catalogue: L("Data catalogue", "فهرس البيانات"),
    demography: L("Population", "السكان"),
    digital: L("Digital", "التحول الرقمي"),
    economy: L("Economy", "الاقتصاد"),
    education: L("Education", "التعليم"),
    energy: L("Energy", "الطاقة"),
    environment: L("Environment", "البيئة"),
    finance: L("Public finance", "المالية العامة"),
    governance: L("Governance", "الحوكمة"),
    health: L("Health", "الصحة"),
    innovation: L("Innovation", "الابتكار"),
    labour: L("Labour market", "سوق العمل"),
    media: L("Media", "الإعلام"),
    resilience: L("Resilience", "الجاهزية والمرونة"),
    tourism: L("Tourism", "السياحة"),
    trade: L("Trade", "التجارة"),
    water: L("Water", "المياه"),
  },
  lessonTag: {
    design: L("Policy design", "تصميم السياسات"),
    evidence: L("Evidence", "الأدلة"),
    consultation: L("Consultation", "الاستشارات"),
    implementation: L("Implementation", "التنفيذ"),
    monitoring: L("Monitoring", "المتابعة"),
    evaluation: L("Evaluation", "التقييم"),
    coordination: L("Coordination", "التنسيق"),
    governance: L("Governance", "الحوكمة"),
    data: L("Data", "البيانات"),
    budget: L("Budget", "الميزانية"),
    legal: L("Legal", "الجوانب القانونية"),
    communication: L("Communication", "الاتصال"),
    stakeholders: L("Stakeholders", "الجهات المعنية"),
    pilot: L("Pilot", "التجربة"),
  },
  recordModule: {
    benchmarks: L("Benchmarks", "المقارنات المعيارية"),
    compliance: L("Compliance", "الامتثال"),
    consultations: L("Consultations", "الاستشارات"),
    crossrefs: L("Cross-references", "الإحالات المرجعية"),
    discovery: L("Discovery", "الاستكشاف"),
    document: L("Document", "الوثيقة"),
    impact: L("Impact", "الأثر"),
    linguistic: L("Language review", "المراجعة اللغوية"),
    me: L("Monitoring and evaluation", "المتابعة والتقييم"),
    options: L("Options", "الخيارات"),
    redlines: L("Redlines", "التعديلات على النص"),
    review: L("Review", "المراجعة"),
    ria: L("Regulatory impact assessment", "تقييم الأثر التنظيمي"),
    situation: L("Situation analysis", "تحليل الوضع الراهن"),
    stakeholders: L("Stakeholders", "الجهات المعنية"),
    tracking: L("Tracking", "التتبع"),
  },
  connectorRunStatus: {
    running: L("Running", "قيد التشغيل"),
    succeeded: L("Succeeded", "اكتمل بنجاح"),
    failed: L("Failed", "تعذّر الإكمال"),
  },
  challengeFindingStatus: {
    open: L("Open", "مفتوحة"),
    addressed: L("Addressed", "عولجت"),
    accepted_risk: L("Risk accepted", "قُبلت المخاطرة"),
    dismissed: L("Dismissed", "استُبعدت"),
  },
  signalSourceKind: {
    news: L("News", "أخبار"),
    social: L("Social media", "وسائل التواصل الاجتماعي"),
    parliament: L("Parliament", "المجلس الوطني الاتحادي"),
    international: L("International", "دولي"),
    data_release: L("Data release", "إصدار بيانات"),
    regulator: L("Regulator", "جهة تنظيمية"),
    radio: L("Radio", "الإذاعة"),
    tv: L("TV", "التلفزيون"),
    open_data: L("Open data", "البيانات المفتوحة"),
  },
  legalStatus: {
    in_force: L("In force", "سارٍ"),
    amended: L("Amended", "معدَّل"),
    repealed: L("Repealed", "ملغى"),
    draft: L("Draft", "مسودة"),
  },
  instrumentType: {
    publicPolicy: L("Public policy", "سياسة عامة"),
    federalLaw: L("Federal law", "قانون اتحادي"),
    decreeLaw: L("Federal decree-law", "مرسوم بقانون اتحادي"),
    cabinetResolution: L("Cabinet resolution", "قرار مجلس الوزراء"),
    executiveRegulation: L("Executive regulation", "لائحة تنفيذية"),
    ministerialResolution: L("Ministerial resolution", "قرار وزاري"),
    strategy: L("Strategy", "استراتيجية"),
    guideline: L("Guideline", "دليل إرشادي"),
    framework: L("Framework", "إطار عام"),
    nationalProgramme: L("National programme", "برنامج وطني"),
    plan: L("Plan", "خطة"),
  },
  classification: {
    public: L("Public", "عام"),
    internal: L("Internal", "داخلي"),
    confidential: L("Confidential", "سري"),
    secret: L("Secret", "سري للغاية"),
  },
  priority: {
    low: L("Low", "منخفضة"),
    normal: L("Normal", "عادية"),
    high: L("High", "مرتفعة"),
    urgent: L("Urgent", "عاجلة"),
  },
  slaHealth: {
    onTrack: L("On track", "ضمن المدة"),
    atRisk: L("At risk", "معرّضة للتأخر"),
    breached: L("Overdue", "متأخرة"),
    paused: L("Paused", "موقوفة"),
    completed: L("Completed", "مكتملة"),
  },
  disclosureLevel: {
    private: L("Private to the ministry", "خاصة بالوزارة"),
    pipelineCard: L("Pipeline card", "بطاقة في المسار الوطني"),
    shared: L("Shared", "مُشارَكة"),
    submitted: L("Submitted", "مقدَّمة"),
    published: L("Published", "منشورة"),
  },
  tenantKind: {
    ministry: L("Ministry workspace", "مساحة عمل الوزارة"),
    oversight: L("GSOC oversight", "رقابة الأمانة العامة لمجلس الوزراء"),
    platform: L("Platform administration", "إدارة المنصة"),
  },
  stageStatus: {
    notStarted: L("Not started", "لم تبدأ"),
    active: L("In progress", "جارية"),
    completed: L("Completed", "مكتملة"),
    returned: L("Returned", "مُعادة"),
    skipped: L("Done outside the platform", "أُنجزت خارج المنصة"),
  },
  gateItemStatus: {
    met: L("Met", "مستوفى"),
    warning: L("Needs attention", "يحتاج إلى متابعة"),
    blocking: L("Blocking", "مانع"),
    info: L("For information", "للعلم"),
  },
  conformanceStatus: {
    met: L("Met", "مستوفى"),
    partial: L("Partly met", "مستوفى جزئياً"),
    missing: L("Missing", "غير مستوفى"),
    notApplicable: L("Not applicable", "لا ينطبق"),
    mandatoryMissing: L("Mandatory item missing", "متطلب إلزامي غير مستوفى"),
  },
  confidenceBand: {
    high: L("High confidence", "درجة ثقة مرتفعة"),
    moderate: L("Moderate confidence", "درجة ثقة متوسطة"),
    low: L("Low confidence", "درجة ثقة منخفضة"),
    very_low: L("Very low confidence", "درجة ثقة منخفضة جداً"),
  },
  proposalConfidence: {
    high: L("High", "مرتفعة"),
    medium: L("Medium", "متوسطة"),
    low: L("Low", "منخفضة"),
  },
  proposalStatus: {
    proposed: L("Proposed", "مقترح"),
    accepted: L("Accepted", "مقبول"),
    edited: L("Accepted with edits", "مقبول مع تعديلات"),
    rejected: L("Rejected", "مرفوض"),
    expired: L("Expired", "منتهي الصلاحية"),
    superseded: L("Superseded", "مستبدَل"),
  },
  agentRunStatus: {
    queued: L("Queued", "في قائمة الانتظار"),
    running: L("Running", "قيد التشغيل"),
    awaitingReview: L("Awaiting review", "بانتظار المراجعة"),
    completed: L("Completed", "مكتمل"),
    failed: L("Failed", "تعذّر الإكمال"),
    cancelled: L("Cancelled", "ملغى"),
  },
  runStepKind: {
    plan: L("Plan", "التخطيط"),
    retrieve: L("Retrieve sources", "استرجاع المصادر"),
    toolCall: L("Tool call", "استدعاء أداة"),
    modelCall: L("AI analysis", "التحليل بالذكاء الاصطناعي"),
    guardrail: L("Guardrail check", "فحص الضوابط"),
    challenge: L("Red-team challenge", "المراجعة النقدية"),
    verify: L("Verify citations", "التحقق من الاستشهادات"),
    propose: L("Proposal", "المقترح"),
    error: L("Error", "خطأ"),
  },
  recordOrigin: {
    manual: L("Entered manually", "إدخال يدوي"),
    aiResearch: L("AI research", "بحث بالذكاء الاصطناعي"),
    ai_research: L("AI research", "بحث بالذكاء الاصطناعي"),
    imported: L("Imported", "مستورد"),
    seed: L("Reference data", "بيانات مرجعية"),
  },
  reviewState: {
    proposed: L("Awaiting review", "بانتظار المراجعة"),
    accepted: L("Accepted", "مقبول"),
    rejected: L("Rejected", "مرفوض"),
  },
  submissionStatus: {
    received: L("Received", "مُستلَمة"),
    inReview: L("In review", "قيد المراجعة"),
    challengeReview: L("Red-team review", "قيد المراجعة النقدية"),
    endorsed: L("Endorsed", "مؤيَّدة"),
    endorsedWithConditions: L("Endorsed with conditions", "مؤيَّدة بشروط"),
    returned: L("Returned", "مُعادة"),
    notSupported: L("Not supported", "غير مؤيَّدة"),
    referredToRegLab: L("Referred to RegLab", "مُحالة إلى مختبر التشريعات"),
    onAgenda: L("On Cabinet agenda", "مدرجة على جدول الأعمال"),
    decided: L("Decided", "صدر القرار"),
  },
  consultationStatus: {
    draft: L("Draft", "مسودة"),
    open: L("Open", "مفتوحة"),
    closed: L("Closed", "مغلقة"),
    synthesised: L("Findings summarised", "لُخّصت النتائج"),
  },
  consultationStance: {
    support: L("Support", "تأييد"),
    support_with_changes: L("Support with changes", "تأييد مع تعديلات"),
    oppose: L("Oppose", "معارضة"),
    neutral: L("No position", "دون موقف"),
  },
  taskStatus: {
    open: L("Open", "مفتوحة"),
    inProgress: L("In progress", "قيد التنفيذ"),
    done: L("Done", "منجزة"),
    cancelled: L("Cancelled", "ملغاة"),
  },
  role: {
    "ws.admin": L("Workspace Admin", "مسؤول مساحة العمل"),
    "policy.owner": L("Policy Owner", "مسؤول السياسة"),
    "policy.analyst": L("Policy Analyst / Drafter", "محلل / صائغ السياسات"),
    "reviewer.legal": L("Legal Reviewer", "مراجع قانوني"),
    "reviewer.finance": L("Finance Reviewer", "مراجع مالي"),
    "reviewer.economic": L("Economic Reviewer", "مراجع اقتصادي"),
    "reviewer.linguistic": L("Linguistic Reviewer", "مراجع لغوي"),
    "approver.director": L("Director (Approver)", "المدير (معتمِد)"),
    "approver.undersecretary": L("Undersecretary (Approver)", "وكيل الوزارة (معتمِد)"),
    "approver.minister": L("Minister (Approver)", "الوزير (معتمِد)"),
    observer: L("Observer", "مراقب"),
    "gsoc.reviewer": L("GSOC Reviewer", "مراجع الأمانة العامة لمجلس الوزراء"),
    "gsoc.director": L("GSOC Director", "مدير في الأمانة العامة لمجلس الوزراء"),
    "gsoc.assistant_secretary_general": L("GSOC Assistant Secretary-General", "الأمين العام المساعد لمجلس الوزراء"),
    "gsoc.secretary_general": L("GSOC Secretary-General", "الأمين العام لمجلس الوزراء"),
    "cabinet.officer": L("Cabinet Affairs Officer", "مسؤول شؤون مجلس الوزراء"),
    "reglab.officer": L("RegLab Officer", "مسؤول مختبر التشريعات"),
    "national.analyst": L("National Analyst", "محلل وطني"),
    "ai.governance": L("AI Governance Officer", "مسؤول حوكمة الذكاء الاصطناعي"),
    auditor: L("Auditor", "مدقق"),
    "platform.admin": L("Platform Admin", "مسؤول المنصة"),
    "security.admin": L("Security Admin", "مسؤول الأمن"),
    "support.breakglass": L("Break-glass Support", "دعم الطوارئ"),
  },
  stage: Object.fromEntries(STAGES.map((s) => [s.key, s.name])) as Record<StageKey, Bilingual>,
  stageShort: Object.fromEntries(STAGES.map((s) => [s.key, s.short])) as Record<StageKey, Bilingual>,
  phase: PHASES,
} satisfies Record<string, Record<string, Bilingual>>;

export type EnumGroup = keyof typeof ENUMS;

/** Knowledge-base document kinds a user can file a document under. */
export const DOCUMENT_KINDS = ["Law", "DecreeLaw", "FederalDecree", "CabinetResolution", "MinisterialResolution", "ExecutiveRegulation", "Strategy", "Policy", "Guideline", "Guidebook", "Manual", "Report", "Statistics", "Lesson", "Other"] as const;

/** Issuing authorities of the legal corpus, keyed by the English name the knowledge base stores. */
export const ISSUERS: Record<string, Bilingual> = {
  "UAE Cabinet": L("UAE Cabinet", "مجلس الوزراء"),
  "Cabinet of Ministers": L("Cabinet of Ministers", "مجلس الوزراء"),
  "President of the United Arab Emirates": L("President of the United Arab Emirates", "رئيس دولة الإمارات العربية المتحدة"),
  "President of the UAE": L("President of the UAE", "رئيس دولة الإمارات العربية المتحدة"),
  "General Secretariat of the Cabinet": L("General Secretariat of the Cabinet", "الأمانة العامة لمجلس الوزراء"),
  "Regulations Lab, General Secretariat of the Cabinet": L("Regulations Lab, General Secretariat of the Cabinet", "مختبر التشريعات، الأمانة العامة لمجلس الوزراء"),
  "Cyber Security Council": L("Cyber Security Council", "مجلس الأمن السيبراني"),
  "Minister of Finance and Industry": L("Minister of Finance and Industry", "وزير المالية والصناعة"),
  "Ministry of Economy and Tourism": L("Ministry of Economy and Tourism", "وزارة الاقتصاد والسياحة"),
  "Ministry of Health and Prevention": L("Ministry of Health and Prevention", "وزارة الصحة ووقاية المجتمع"),
};

/** Units of measure the indicators and data series store, keyed by the English unit. Units outside the list are shown as entered. */
export const UNITS: Record<string, Bilingual> = {
  "USD": L("USD", "دولار أمريكي"),
  "years": L("years", "سنوات"),
  "index": L("index", "مؤشر"),
  "index (0-100)": L("index (0-100)", "مؤشر (0-100)"),
  "index (projected counterfactual = 100)": L("index (projected counterfactual = 100)", "مؤشر (السيناريو المرجعي المتوقع = 100)"),
  "% of GDP": L("% of GDP", "% من الناتج المحلي الإجمالي"),
  "% of capacity": L("% of capacity", "% من السعة"),
  "percentage points": L("percentage points", "نقاط مئوية"),
  "MW": L("MW", "ميجاواط"),
  "GWh": L("GWh", "جيجاواط ساعة"),
  "kWh per capita": L("kWh per capita", "كيلوواط ساعة للفرد"),
  "kWh/m2/day": L("kWh/m2/day", "كيلوواط ساعة/م²/يوم"),
  "°C": L("°C", "°م"),
  "t CO2 per capita": L("t CO2 per capita", "طن ثاني أكسيد الكربون للفرد"),
  "MtCO2e": L("MtCO2e", "مليون طن مكافئ ثاني أكسيد الكربون"),
  "MtCO2e a year": L("MtCO2e a year", "مليون طن مكافئ ثاني أكسيد الكربون سنوياً"),
  "MtCO2e per year": L("MtCO2e per year", "مليون طن مكافئ ثاني أكسيد الكربون سنوياً"),
  "persons": L("persons", "أشخاص"),
  "arrivals": L("arrivals", "زائر"),
  "per 1,000 people": L("per 1,000 people", "لكل 1,000 شخص"),
  "per 100,000 live births": L("per 100,000 live births", "لكل 100,000 مولود حي"),
  "m3 per capita": L("m3 per capita", "م³ للفرد"),
  "billion m³/year": L("billion m³/year", "مليار م³ سنوياً"),
  "L/person/day": L("L/person/day", "لتر للفرد يومياً"),
  "USD/m³": L("USD/m³", "دولار أمريكي/م³"),
  "g per 100 ml": L("g per 100 ml", "جم لكل 100 مل"),
  "AED million": L("AED million", "مليون درهم"),
  "AED million a year": L("AED million a year", "مليون درهم سنوياً"),
  "AED million per year": L("AED million per year", "مليون درهم سنوياً"),
  "AED trillion": L("AED trillion", "تريليون درهم"),
  "AED/kWh": L("AED/kWh", "درهم/كيلوواط ساعة"),
  "AED per person-day": L("AED per person-day", "درهم للفرد يومياً"),
  "KRW trillion": L("KRW trillion", "تريليون وون كوري"),
  "days": L("days", "أيام"),
  "months": L("months", "أشهر"),
  "hours": L("hours", "ساعات"),
  "sites": L("sites", "مواقع"),
  "count": L("count", "عدد"),
  "probability": L("probability", "احتمال"),
  "datasets": L("datasets", "مجموعات بيانات"),
  "%": L("%", "%"),
  "% of population": L("% of population", "% من السكان"),
  "% of GDP (around)": L("% of GDP (around)", "% من الناتج المحلي الإجمالي (تقريباً)"),
  "% of skilled jobs": L("% of skilled jobs", "% من الوظائف الماهرة"),
  "% reduction": L("% reduction", "% خفض"),
  "% reduction vs 2019": L("% reduction vs 2019", "% خفض مقارنة بعام 2019"),
  "% improvement vs 2019 (band 42–45)": L("% improvement vs 2019 (band 42–45)", "% تحسّن مقارنة بعام 2019 (النطاق 42–45)"),
  "AED billion": L("AED billion", "مليار درهم"),
  "AED billion (up to)": L("AED billion (up to)", "مليار درهم (حتى)"),
  "USD per cubic metre": L("USD per cubic metre", "دولار أمريكي للمتر المكعب"),
  "GW (more than)": L("GW (more than)", "جيجاواط (أكثر من)"),
  "kg CO2 per kWh (approximately)": L("kg CO2 per kWh (approximately)", "كجم ثاني أكسيد الكربون لكل كيلوواط ساعة (تقريباً)"),
  "million tonnes per annum": L("million tonnes per annum", "مليون طن سنوياً"),
  "net emissions (net zero)": L("net emissions (net zero)", "صافي الانبعاثات (الحياد المناخي)"),
  "litres per person per day": L("litres per person per day", "لتر للفرد يومياً"),
  "million trees": L("million trees", "مليون شجرة"),
  "million guests": L("million guests", "مليون نزيل"),
  "global rank (position or better)": L("global rank (position or better)", "الترتيب العالمي (المركز أو أفضل)"),
  "degrees (index points reduction)": L("degrees (index points reduction)", "درجات (خفض نقاط المؤشر)"),
  "multiple of current contribution": L("multiple of current contribution", "ضعف المساهمة الحالية"),
  "jobs": L("jobs", "وظيفة"),
  "jobs (more than)": L("jobs (more than)", "وظيفة (أكثر من)"),
  "workers (about)": L("workers (about)", "عامل (تقريباً)"),
  "Emirati employees per company": L("Emirati employees per company", "موظف إماراتي لكل شركة"),
  "SMEs": L("SMEs", "مشروع صغير أو متوسط"),
  "startups": L("startups", "شركة ناشئة"),
  "unicorns": L("unicorns", "شركة يونيكورن"),
  "hydrogen oases": L("hydrogen oases", "واحة هيدروجين"),
  "interconnection points": L("interconnection points", "نقطة ربط"),
  "procedures (minimum)": L("procedures (minimum)", "إجراءات (حد أدنى)"),
};

/** Builds the `enums` subtree of an i18next catalogue for one language. */
export function enumCatalogue(locale: "en" | "ar"): Record<string, Record<string, string>> {
  const out: Record<string, Record<string, string>> = {};
  for (const [group, values] of Object.entries(ENUMS)) {
    out[group] = Object.fromEntries(Object.entries(values as Record<string, Bilingual>).map(([k, v]) => [k, v[locale]]));
  }
  return out;
}
